(() => {
  if (window.SfCarousel) return;
  class SfCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort && !this.abort.signal.aborted) return;
      this.abort = new AbortController();
      this.slides = [...this.querySelectorAll('[data-native-slide], [data-story-slide]')];
      this.dots = [...this.querySelectorAll('[data-native-dot], [data-story-dot]')];
      this.progressTracks = this.dots.map((dot) => {
        const track = dot.querySelector('[data-carousel-track]') || document.createElement('span');
        track.setAttribute('data-carousel-track', '');
        track.setAttribute('aria-hidden', 'true');
        const bar = dot.querySelector('[data-carousel-progress]') || document.createElement('span');
        bar.setAttribute('data-carousel-progress', '');
        bar.setAttribute('aria-hidden', 'true');
        track.append(bar);
        if (!track.parentNode) dot.append(track);
        return track;
      });
      this.progressBars = this.progressTracks.map((track) => track.firstElementChild);
      this.warmedImages = new WeakSet();
      this.index = 0;
      this.duration = Math.max(4, Math.min(10, Number(this.dataset.interval) || 6)) * 1000;
      this.remaining = this.duration;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.visible = false;
      this.paused = false;
      this.reasons = new Set();
      const opts = { signal: this.abort.signal };
      this.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('button');
          if (!button) return;
          if (button.hasAttribute('data-carousel-pause')) {
            this.paused = !this.paused;
            // An explicit Play action starts playback even while its control has focus.
            if (!this.paused) {
              clearTimeout(this.resumeTimer);
              for (const reason of ['focus', 'hover', 'interaction']) this.reasons.delete(reason);
            }
            this.syncPause();
            this.run();
            return;
          }
          let next = null;
          if (button.hasAttribute('data-native-dot')) next = Number(button.dataset.nativeDot);
          if (button.hasAttribute('data-story-dot')) next = Number(button.dataset.storyDot);
          if (button.hasAttribute('data-native-prev')) next = this.index - 1;
          if (button.hasAttribute('data-native-next')) next = this.index + 1;
          if (button.dataset.storyDirection) next = this.index + Number(button.dataset.storyDirection);
          if (next !== null) {
            this.show(next, event.detail === 0);
            this.delayResume();
          }
        },
        opts,
      );
      this.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'touch') this.hold('hover', true);
        },
        opts,
      );
      this.addEventListener('pointerleave', () => this.hold('hover', false), opts);
      this.addEventListener('focusin', () => this.hold('focus', true), opts);
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.hold('focus', false);
        },
        opts,
      );
      this.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType !== 'touch' || event.target.closest('a,button,input,select,textarea')) return;
          this.swipe = [event.clientX, event.clientY];
          this.hold('touch', true);
        },
        opts,
      );
      document.addEventListener(
        'pointerup',
        (event) => {
          if (!this.swipe) return;
          const dx = event.clientX - this.swipe[0];
          const dy = event.clientY - this.swipe[1];
          this.swipe = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
          this.hold('touch', false);
        },
        opts,
      );
      document.addEventListener(
        'pointercancel',
        () => {
          this.swipe = null;
          this.hold('touch', false);
        },
        opts,
      );
      this.addEventListener(
        'keydown',
        (event) => {
          if (!event.target.closest('.live-slide-controls, .sf8-story-controls') && event.target !== this) return;
          const next = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[
            event.key
          ];
          if (next === undefined) return;
          event.preventDefault();
          this.show(next, true);
          this.dots[this.index]?.focus({ preventScroll: true });
          this.delayResume();
        },
        opts,
      );
      document.addEventListener('visibilitychange', () => this.run(), opts);
      this.reduced.addEventListener('change', () => this.run(), opts);
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.slides.findIndex(
            (slide) => slide.contains(event.target) || slide.dataset.blockId === event.detail?.blockId,
          );
          if (index < 0) return;
          this.hold('editor', true);
          this.show(index, true);
        },
        opts,
      );
      document.addEventListener('shopify:block:deselect', () => this.hold('editor', false), opts);
      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0]?.isIntersecting || false;
          if (this.visible) this.warmSlides();
          this.run();
        },
        { threshold: 0.1 },
      );
      this.observer.observe(this);
      this.querySelectorAll('[data-carousel-pause]').forEach(
        (button) => (button.hidden = !this.autoplay || this.slides.length < 2),
      );
      this.show(0, true);
      this.syncPause();
    }
    show(next, instant = false) {
      if (!this.slides.length) return;
      this.stop();
      this.index = ((next % this.slides.length) + this.slides.length) % this.slides.length;
      this.remaining = this.duration;
      this.classList.toggle('instant', instant || this.reduced.matches);
      this.slides.forEach((slide, index) => {
        slide.hidden = index !== this.index;
        slide.classList.toggle('is-current', index === this.index);
      });
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-pressed', String(index === this.index));
        dot.setAttribute('aria-current', String(index === this.index));
      });
      this.progressTracks.forEach((track, index) => {
        track.hidden = index !== this.index;
      });
      this.progressBars.forEach((bar, index) => {
        bar.hidden = index !== this.index;
        bar.style.transform = 'scaleX(0)';
      });
      const counter = this.querySelector('[data-story-count], .live-slide-count');
      if (counter)
        counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      const slide = this.slides[this.index];
      if (slide.style.getPropertyValue('--story-bg'))
        this.style.setProperty('--story-bg', slide.style.getPropertyValue('--story-bg'));
      if (slide.dataset.controlsInk) this.style.setProperty('--story-controls-ink', slide.dataset.controlsInk);
      if (slide.dataset.controlsBackground)
        this.style.setProperty('--slide-controls-bg', slide.dataset.controlsBackground);
      if (slide.dataset.controlsForeground)
        this.style.setProperty('--slide-controls-ink', slide.dataset.controlsForeground);
      if (this.visible) this.warmSlides();
      this.run();
    }
    warmSlides() {
      for (const index of [this.index, (this.index + 1) % this.slides.length]) {
        this.slides[index]?.querySelectorAll('img').forEach((image) => {
          if (!this.warmedImages.has(image)) {
            this.warmedImages.add(image);
            image.dataset.carouselReady = String(image.complete && image.naturalWidth > 0);
            const reveal = () => {
              image.dataset.carouselReady = 'true';
            };
            image.addEventListener('load', reveal, { signal: this.abort.signal, once: true });
            image.addEventListener('error', reveal, { signal: this.abort.signal, once: true });
          }
          image.loading = 'eager';
          image.decoding = 'async';
        });
      }
    }
    stop() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.remaining = Math.max(0, this.remaining - (performance.now() - this.started));
      }
      this.timer = null;
      this.progressAnimation?.cancel();
      this.progressAnimation = null;
      const bar = this.progressBars[this.index];
      if (bar) bar.style.transform = `scaleX(${1 - this.remaining / this.duration})`;
      this.dataset.running = 'false';
    }
    hold(reason, state) {
      if (state) this.reasons.add(reason);
      else this.reasons.delete(reason);
      if (state) this.run();
      else this.delayResume();
    }
    delayResume() {
      this.reasons.add('interaction');
      this.run();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.reasons.delete('interaction');
        this.run();
      }, 3000);
    }
    run() {
      const allowed =
        this.autoplay &&
        !this.paused &&
        !this.reduced.matches &&
        this.visible &&
        !document.hidden &&
        !this.reasons.size &&
        this.slides.length > 1;
      if (!allowed) {
        this.stop();
        return;
      }
      if (this.timer) return;
      this.started = performance.now();
      this.dataset.running = 'true';
      const bar = this.progressBars[this.index];
      if (bar && typeof bar.animate === 'function') {
        this.progressAnimation = bar.animate(
          [{ transform: `scaleX(${1 - this.remaining / this.duration})` }, { transform: 'scaleX(1)' }],
          { duration: this.remaining, fill: 'forwards', easing: 'linear' },
        );
      }
      this.timer = setTimeout(() => {
        this.timer = null;
        this.show(this.index + 1);
      }, this.remaining);
    }
    syncPause() {
      this.querySelectorAll('[data-carousel-pause]').forEach((button) => {
        button.setAttribute('aria-label', this.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
        button.setAttribute('aria-pressed', String(this.paused));
        button.innerHTML = `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${this.paused ? '<path d="m9 5 10 7-10 7Z"/>' : '<path d="M9 5v14M15 5v14"/>'}</svg>`;
      });
    }
    disconnectedCallback() {
      this.stop();
      clearTimeout(this.resumeTimer);
      this.abort?.abort();
      this.observer?.disconnect();
    }
  }
  window.SfCarousel = SfCarousel;
  for (const name of ['sf-slideshow', 'sf8-story']) {
    if (!customElements.get(name)) customElements.define(name, class extends SfCarousel {});
  }
})();
